<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PAGE_ICONS } from '@/navigation/navIcons';
import { modelIcon } from '@/composables/modelIcons';
import type { AreaPicker } from '@/navigation/areaPicker';

/**
 * The one menu at the left of the desktop header, for example "Zones ▾". It
 * lists what the sidebar lists: the area's record types, its pages and your
 * saved views there, with a check on the one you are on. Every item is a
 * link, so Cmd-click opens it in a new tab.
 *
 * On a list page the whole label opens the menu. On a record page the label
 * is a link back to the list, given as `labelTo`, and the arrow beside it
 * opens the menu. With nowhere else to go, there is no menu: the label is
 * plain text, or a plain link on a record page.
 */

const props = defineProps<{
    picker: AreaPicker;
    label: string;
    labelTo?: RouteLocationRaw;
}>();

// Each record type's icon, looked up once rather than on every use in the template.
const recordItemsWithIcons = computed(() => props.picker.recordItems.map((item) => ({ ...item, icon: modelIcon(item.entityId) })));
</script>

<template>
  <template v-if="!props.picker.hasChoice">
    <router-link v-if="props.labelTo" :to="props.labelTo" :title="props.label" class="min-w-0 truncate px-1.5 text-muted-foreground hover:text-foreground" data-testid="breadcrumb-page-link">
      {{ props.label }}
    </router-link>
    <span v-else :title="props.label" class="min-w-0 truncate px-1.5 font-medium" data-testid="breadcrumb-page-label">{{ props.label }}</span>
  </template>
  <DropdownMenu v-else>
    <router-link v-if="props.labelTo" :to="props.labelTo" :title="props.label" class="min-w-0 truncate rounded-md px-1.5 text-muted-foreground hover:text-foreground" data-testid="breadcrumb-page-link">
      {{ props.label }}
    </router-link>
    <DropdownMenuTrigger as-child>
      <button
        v-if="props.labelTo"
        type="button"
        aria-label="Other lists in this area"
        data-testid="breadcrumb-page-arrow"
        class="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted"
      >
        <ChevronDownIcon class="size-3" aria-hidden="true" />
      </button>
      <button
        v-else
        type="button"
        data-testid="breadcrumb-page"
        class="group inline-flex h-7 min-w-0 items-center gap-1 rounded-md px-1.5 font-medium hover:bg-muted data-[state=open]:bg-muted"
      >
        <span :title="props.label" class="min-w-0 truncate">{{ props.label }}</span>
        <ChevronDownIcon class="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-data-[state=open]:opacity-100" aria-hidden="true" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" class="max-h-[70vh] min-w-60 overflow-y-auto">
      <DropdownMenuItem v-for="item in recordItemsWithIcons" :key="item.key" as-child>
        <router-link :to="item.to" :aria-current="item.current ? 'page' : undefined" class="flex items-center gap-2.5" data-testid="breadcrumb-page-item">
          <component :is="item.icon" v-if="item.icon" class="size-4" aria-hidden="true" />
          <span class="flex-1 truncate">{{ item.label }}</span>
          <CheckIcon v-if="item.current" class="size-4" aria-hidden="true" />
        </router-link>
      </DropdownMenuItem>
      <template v-if="props.picker.pageItems.length > 0">
        <DropdownMenuSeparator v-if="recordItemsWithIcons.length > 0" />
        <DropdownMenuItem v-for="item in props.picker.pageItems" :key="item.key" as-child>
          <router-link :to="item.to" :aria-current="item.current ? 'page' : undefined" class="flex items-center gap-2.5" data-testid="breadcrumb-page-item">
            <component :is="PAGE_ICONS[item.routeName]" v-if="PAGE_ICONS[item.routeName]" class="size-4 text-muted-foreground" aria-hidden="true" />
            <span class="flex-1 truncate">{{ item.label }}</span>
            <CheckIcon v-if="item.current" class="size-4" aria-hidden="true" />
          </router-link>
        </DropdownMenuItem>
      </template>
      <template v-if="props.picker.viewItems.length > 0">
        <DropdownMenuSeparator />
        <DropdownMenuLabel class="text-xs font-medium text-muted-foreground">Saved views</DropdownMenuLabel>
        <DropdownMenuItem v-for="item in props.picker.viewItems" :key="item.key" as-child>
          <router-link :to="item.to" :aria-current="item.current ? 'page' : undefined" class="flex items-center gap-2.5" data-testid="breadcrumb-view-item">
            <SavedViewIcon :appearance="item.view" class="size-4" />
            <span class="flex-1 truncate">{{ item.label }}</span>
            <CheckIcon v-if="item.current" class="size-4" aria-hidden="true" />
          </router-link>
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
